import { useState, useEffect, useCallback, useMemo } from 'react';
import { DEFAULT_SEKA_CONFIG } from '../game/sekaEngine';
import { shouldShowLobbyTablesSocketErrorBanner } from '../realtime/connectionUx';

const LOBBY_JOIN_TABLE_ACK = 'lobby/join-table';
const LOBBY_CREATE_TABLE_ACK = 'lobby/create-table';

const COLYSEUS_TABLE_MODES = new Set(['seka', 'poker', 'belot']);

/**
 * Если ACK не содержит gameRoomId, ждём следующий снимок лобби (после join_table сервер шлёт LOBBY_STATE).
 */
function waitGameRoomIdFromLobby(realtimeSocket, tableId, timeoutMs = 5000) {
  const tid = String(tableId || '')
    .trim()
    .toUpperCase();
  if (!tid || !realtimeSocket?.on) return Promise.resolve('');
  return new Promise((resolve) => {
    let done = false;
    const finish = (gid) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      try {
        realtimeSocket.off('lobby_update', onUpdate);
      } catch {
        /* ignore */
      }
      resolve(String(gid || '').trim());
    };
    const onUpdate = (data) => {
      const tables = Array.isArray(data?.tables) ? data.tables : [];
      const row = tables.find(
        (t) =>
          String(t?.id ?? t?.tableId ?? '')
            .trim()
            .toUpperCase() === tid
      );
      const gid = row && String(row.gameRoomId || '').trim();
      if (gid) finish(gid);
    };
    const timer = setTimeout(() => finish(''), timeoutMs);
    realtimeSocket.on('lobby_update', onUpdate);
    realtimeSocket.emit('join_lobby');
  });
}

/**
 * Состояние и действия для блока «Столы» (Сека) — общий для Lobby и PokerLobby.
 * Реалтайм только через Colyseus (`socket` = ColyseusLobbyTransport из App).
 * @param {{ lobbyMode?: 'seka'|'poker'|'belot', lobbyReturnView?: string, onCreateRoomAck?: (info: { tableId: string, isPrivate: boolean, roomPassword: string }) => void }} [options]
 */
export function useLobbySekaTables({
  socket,
  user,
  onJoinTable,
  lobbyMode = 'seka',
  lobbyReturnView = 'lobby',
  onCreateRoomAck,
}) {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [tablesLoaded, setTablesLoaded] = useState(false);

  const realtimeSocket = socket;

  const fetchTables = useCallback(async () => {
    if (realtimeSocket?.connected) {
      realtimeSocket.emit('join_lobby');
    }
    setTablesLoaded(true);
  }, [realtimeSocket]);

  const onRefreshTables = useCallback(async () => {
    setRefreshing(true);
    await fetchTables();
    setRefreshing(false);
  }, [fetchTables]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  useEffect(() => {
    if (realtimeSocket?.connected) {
      realtimeSocket.emit('join_lobby');

      const handleLobbyUpdate = (data) => {
        if (data && data.tables) setTables(data.tables);
      };

      const handleTableUpdate = (data) => {
        if (data && data.table) {
          const nid = String(data.table.id || '').toUpperCase();
          setTables((prev) =>
            prev.map((t) => (String(t.id || '').toUpperCase() === nid ? data.table : t))
          );
        }
      };

      const handleTableCreated = (data) => {
        if (data && data.table) setTables((prev) => [...prev, data.table]);
      };

      const handleBotRoomCreated = (data) => {
        if (data && data.table) {
          setTables((prev) => [...prev, data.table]);
          onJoinTable({
            tableId: data.table.id,
            gameRoomId: data.table.gameRoomId || '',
            mode: data.table.mode || lobbyMode,
            returnView: lobbyReturnView,
          });
        }
      };

      const handleSocketError = (err) => {
        const payload = err && typeof err === 'object' ? err : { message: String(err || '') };
        if (!shouldShowLobbyTablesSocketErrorBanner(payload)) return;
        const msg =
          typeof payload.message === 'string' && payload.message.trim()
            ? payload.message.trim()
            : 'Ошибка лобби';
        setError(msg);
        setTimeout(() => setError(''), 5000);
      };

      const handleTableRemoved = (data) => {
        const removedId = String(data?.tableId || '')
          .trim()
          .toUpperCase();
        if (!removedId) return;
        setTables((prev) => prev.filter((t) => String(t.id || '').toUpperCase() !== removedId));
      };

      realtimeSocket.on('lobby_update', handleLobbyUpdate);
      realtimeSocket.on('table_update', handleTableUpdate);
      realtimeSocket.on('table_created', handleTableCreated);
      realtimeSocket.on('bot_room_created', handleBotRoomCreated);
      realtimeSocket.on('table_removed', handleTableRemoved);
      realtimeSocket.on('error', handleSocketError);

      return () => {
        realtimeSocket.off('lobby_update', handleLobbyUpdate);
        realtimeSocket.off('table_update', handleTableUpdate);
        realtimeSocket.off('table_created', handleTableCreated);
        realtimeSocket.off('bot_room_created', handleBotRoomCreated);
        realtimeSocket.off('table_removed', handleTableRemoved);
        realtimeSocket.off('error', handleSocketError);
      };
    }
    return undefined;
  }, [realtimeSocket, onJoinTable, lobbyMode, lobbyReturnView]);

  const createRoomFromModal = useCallback(
    async (form, onSuccess) => {
      const name = String(form?.name || '').trim();
      if (!name) {
        setError('Введите название комнаты');
        return;
      }
      const isPrivate = Boolean(form?.isPrivate);
      const password = String(form?.roomPassword ?? '').trim();
      if (isPrivate && password.length < 4) {
        setError('Пароль комнаты: минимум 4 символа');
        return;
      }
      if (isPrivate && password.length > 128) {
        setError('Пароль комнаты: не длиннее 128 символов');
        return;
      }
      const isBucks = (form?.currency || 'seka') === 'bucks';
      const minBuyIn = isBucks ? 0.5 : 10;
      const maxBuyIn = isBucks ? 10000 : 1000000;
      const buyInStep = isBucks ? 0.5 : 10;
      const rawBuyIn = Number(form?.buyIn);
      if (!Number.isFinite(rawBuyIn)) {
        setError('Некорректная сумма входа');
        return;
      }
      const normalizedBuyIn = Number(
        (Math.max(minBuyIn, Math.min(maxBuyIn, Math.round(rawBuyIn / buyInStep) * buyInStep))).toFixed(
          isBucks ? 2 : 0
        )
      );
      const availableBalance = isBucks
        ? Number(user?.bucksBalance ?? user?.realBalance ?? user?.cashBalance ?? 0)
        : Number(user?.balance ?? 0);
      if (!Number.isFinite(availableBalance) || normalizedBuyIn > availableBalance) {
        setError('Недостаточно средств для такого входа');
        return;
      }

      if (!realtimeSocket?.connected) {
        setError('Нет соединения с лобби');
        return;
      }

      setLoading(true);
      setError('');

      try {
        const userAtTable = tables.find((table) =>
          table.players.some((player) => player.username === user.username)
        );

        if (userAtTable) {
          const confirmLeave = window.confirm(
            `Вы уже за столом «${userAtTable.name}». Покинуть и создать новый?`
          );
          if (confirmLeave) {
            realtimeSocket.emit('leave_table', {
              tableId: String(userAtTable.id || '')
                .trim()
                .toUpperCase(),
            });
          } else {
            setLoading(false);
            return;
          }
        }

        const anteParsed = Number(form?.ante);
        const anteForTable =
          lobbyMode === 'seka'
            ? Math.max(
                1,
                Math.min(
                  Math.floor(
                    Number.isFinite(anteParsed) && anteParsed > 0 ? anteParsed : DEFAULT_SEKA_CONFIG.ante
                  ),
                  Math.floor(normalizedBuyIn) || 1
                )
              )
            : undefined;

        const payload = {
          name,
          currency: form?.currency || 'seka',
          maxPlayers: form.maxPlayers,
          buyIn: normalizedBuyIn,
          ...(lobbyMode === 'seka' && anteForTable != null ? { ante: anteForTable } : {}),
          ...(isPrivate
            ? {
                isPrivate: true,
                password,
              }
            : {}),
        };

        const tableId = await new Promise((resolve, reject) => {
          let settled = false;
          const cleanup = () => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            try {
              realtimeSocket.off('ack', onAck);
            } catch {
              /* ignore */
            }
          };
          const onAck = (p) => {
            const cmd = String(p?.command || '');
            const tid = String(p?.tableId || '')
              .trim()
              .toUpperCase();
            if (cmd !== LOBBY_CREATE_TABLE_ACK || !tid) return;
            cleanup();
            resolve(tid);
          };
          const timer = setTimeout(() => {
            cleanup();
            reject(new Error('CREATE_TIMEOUT'));
          }, 12000);
          realtimeSocket.on('ack', onAck);
          realtimeSocket.emit('create_table', { ...payload, mode: lobbyMode });
        });

        onCreateRoomAck?.({
          tableId,
          isPrivate,
          roomPassword: isPrivate ? password : '',
        });
        onSuccess?.();
      } catch (err) {
        const msg = err?.message;
        if (msg === 'CREATE_TIMEOUT') {
          setError('Не удалось подтвердить создание комнаты. Проверьте список столов.');
        } else {
          setError(err.response?.data?.message || 'Ошибка создания стола');
        }
      } finally {
        setLoading(false);
      }
    },
    [tables, user, realtimeSocket, lobbyMode, onCreateRoomAck]
  );

  const handleJoinTable = useCallback(
    async (tableOrId) => {
      if (!realtimeSocket?.connected) {
        setError('Нет соединения с лобби');
        return;
      }
      const fromObj = typeof tableOrId === 'object' && tableOrId !== null;
      const tableId = String(fromObj ? tableOrId.id ?? '' : tableOrId ?? '')
        .trim()
        .toUpperCase();
      if (!tableId) return;

      setLoading(true);
      setError('');
      try {
        const { gameRoomId, mode: ackMode } = await new Promise((resolve) => {
          const onAck = (p) => {
            const ackTid = String(p?.tableId ?? '')
              .trim()
              .toUpperCase();
            if (p?.command === LOBBY_JOIN_TABLE_ACK && ackTid === tableId) {
              realtimeSocket.off('ack', onAck);
              clearTimeout(timer);
              resolve({
                gameRoomId: String(p?.gameRoomId || ''),
                mode: String(p?.mode || ''),
              });
            }
          };
          const timer = setTimeout(() => {
            realtimeSocket.off('ack', onAck);
            resolve({
              gameRoomId: fromObj ? String(tableOrId.gameRoomId || '').trim() : '',
              mode: '',
            });
          }, 5000);
          realtimeSocket.on('ack', onAck);
          realtimeSocket.emit('join_table', { tableId });
        });
        const mode =
          ackMode ||
          (fromObj ? String(tableOrId.mode || lobbyMode) : String(lobbyMode));
        let resolvedGameRoomId = String(gameRoomId || '').trim();
        const fallbackGid = fromObj ? String(tableOrId.gameRoomId || '').trim() : '';
        resolvedGameRoomId = resolvedGameRoomId || fallbackGid;
        if (!resolvedGameRoomId && COLYSEUS_TABLE_MODES.has(String(mode || lobbyMode).toLowerCase())) {
          resolvedGameRoomId = await waitGameRoomIdFromLobby(realtimeSocket, tableId);
        }
        if (!resolvedGameRoomId && COLYSEUS_TABLE_MODES.has(String(mode || lobbyMode).toLowerCase())) {
          setError('Не удалось получить игровую комнату. Обновите список столов и попробуйте снова.');
          setLoading(false);
          return;
        }
        onJoinTable({
          tableId,
          gameRoomId: resolvedGameRoomId,
          mode,
          returnView: lobbyReturnView,
        });
      } catch (err) {
        setError(err.response?.data?.message || 'Ошибка присоединения');
        const mode = fromObj ? String(tableOrId.mode || lobbyMode) : String(lobbyMode);
        let gid = fromObj ? String(tableOrId.gameRoomId || '').trim() : '';
        if (!gid && COLYSEUS_TABLE_MODES.has(String(mode).toLowerCase())) {
          gid = await waitGameRoomIdFromLobby(realtimeSocket, tableId, 3500);
        }
        if (gid || !COLYSEUS_TABLE_MODES.has(String(mode).toLowerCase())) {
          onJoinTable({
            tableId,
            gameRoomId: gid,
            mode,
            returnView: lobbyReturnView,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [realtimeSocket, onJoinTable, lobbyMode, lobbyReturnView]
  );

  const handleLeaveTable = useCallback(
    async (tableId) => {
      if (!realtimeSocket?.connected) return;
      try {
        const tid = String(tableId || '')
          .trim()
          .toUpperCase();
        if (!tid) return;
        realtimeSocket.emit('leave_table', { tableId: tid });
      } catch (err) {
        setError(err.response?.data?.message || 'Ошибка выхода со стола');
      }
    },
    [realtimeSocket]
  );

  const formatTableName = useCallback((table) => table.name || `Стол #${table.id}`, []);

  const getStatusText = useCallback((status) => {
    switch (status) {
      case 'waiting':
        return 'Ожидание';
      case 'playing':
        return 'Идёт игра';
      default:
        return 'Ожидание';
    }
  }, []);

  const memoizedTables = useMemo(() => tables || [], [tables]);

  return {
    tables: memoizedTables,
    tablesLoaded,
    loading,
    refreshing,
    error,
    setError,
    onRefreshTables,
    createRoomFromModal,
    handleJoinTable,
    handleLeaveTable,
    formatTableName,
    getStatusText,
  };
}
